import React from "react";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import { useLocale } from "@insightfulscience/smart-react/i18";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import propsResolver from "@insightfulscience/shared-utils/fn/props-resolver";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import HeroBackground from "@insightfulscience/atomic-react/Hero/HeroBackground";
import RecommendedResourcesPost from "@insightfulscience/atomic-react/RecommendedResources/Post";
import RecommendedResourcesPostItem from "@insightfulscience/atomic-react/RecommendedResources/PostItem";
import Block from "@insightfulscience/atomic-react/Block";
import Image from "@insightfulscience/atomic-react/Image";
import Link from "@insightfulscience/atomic-react/Link";
import Icon from "@insightfulscience/atomic-react/Icon";
import SiteLayout from "../../SiteLayout";
import WithServices from "../../services";
import { countries } from "../PurchaseBilling/resolvers";
import withErrorHandling from "../../AppError/withErrorHandling";
import ContactForm from "../../ContactForm";

const sections = [
	{
		image: "SECTION_1_IMAGE",
		title: "SECTION_1_TITLE",
		description: "SECTION_1_DESCRIPTION",
		link: "SECTION_1_LINK",
		url: "SECTION_1_URL",
	},
	{
		image: "SECTION_2_IMAGE",
		title: "SECTION_2_TITLE",
		description: "SECTION_2_DESCRIPTION",
		link: "SECTION_2_LINK",
		url: "SECTION_2_URL",
	},
	{
		image: "SECTION_3_IMAGE",
		title: "SECTION_3_TITLE",
		description: "SECTION_3_DESCRIPTION",
		link: "SECTION_3_LINK",
		url: "SECTION_3_URL",
	},
];

const Contact = () => {
	const [t] = useLocale("contact");
	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.OGP title={t("META.TITLE")} description={t("META.DESCRIPTION")} />
				<PageMeta.Canonical href={t("META.CANONICAL")} />
			</PageMeta>
			<HeroBackground.Images
				color="4"
				images={[t("HERO_IMAGE_1"), t("HERO_IMAGE_2")]}
				imagePositions={["left center", "90% center"]}
			>
				<Hero sx={{ textAlign: "left" }} color="transparent" spacing={{ py: 18 }}>
					<HeroRow mx center={false} utility={{ align: { items: "center" } }}>
						<HeroCol md={12} lg={6} smOrder={1} mdOrder={0}>
							<HeroTitle
								spacing={{ sm: { mt: 6 }, md: { mt: 0 } }}
								sx={{ fontColor: "black", textAlign: "left" }}
							>
								{t("HERO_TITLE")}
							</HeroTitle>
							<HeroDescription sx={{ fontColor: "black", textAlign: "left" }}>
								{t("HERO_DESCRIPTION")}
							</HeroDescription>
						</HeroCol>
					</HeroRow>
				</Hero>
			</HeroBackground.Images>
			<LandingSimpleSection isBorderNone>
				<RecommendedResourcesPost justify>
					{sections.map(({ image, title, description, link, url }) => (
						<RecommendedResourcesPostItem
							key={t(title)}
							lg={4}
							utility={{ sm: { border: { left: { width: 0 } } } }}
						>
							<Block>
								<Image src={t(image)} />
							</Block>
							<Paragraph sx={{ fontSize: "xl", fontWeight: 500 }}>{t(title)}</Paragraph>
							<Paragraph sx={{ fontColor: "black--60" }} spacing={{ mt: 2 }}>
								{t(description)}
							</Paragraph>
							<Link
								href={t(url)}
								spacing={{ mt: 3 }}
								sx={{ fontColor: "black", fontWeight: 500, hover: { fontColor: "3" } }}
							>
								{t(link)} <Icon name="angle-right" spacing={{ ml: 1 }} color="3" />
							</Link>
						</RecommendedResourcesPostItem>
					))}
				</RecommendedResourcesPost>
			</LandingSimpleSection>
			<LandingSimpleSection isBorderNone spacing={{ pt: 0 }}>
				<LandingSimpleSection.Title spacing={{ mb: 12 }}>
					{t("LOCATIONS")}
				</LandingSimpleSection.Title>
				<RecommendedResourcesPost>
					<RecommendedResourcesPostItem lg={4} utility={{ sm: { border: { left: { width: 0 } } } }}>
						<Block>
							<Image src={t("SECTION_4_IMAGE")} />
						</Block>
						<Paragraph sx={{ fontSize: "xl", fontWeight: 500 }}>{t("SECTION_4_TITLE")}</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }} spacing={{ mt: 2 }}>
							{t("SECTION_4_PARAGRAPH_1")}
						</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }}>{t("SECTION_4_PARAGRAPH_2")}</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }}>{t("SECTION_4_PARAGRAPH_3")}</Paragraph>
						<Paragraph sx={{ fontColor: "black--60", fontWeight: "bold" }}>
							{t("SECTION_4_PARAGRAPH_4")}
						</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }}>{t("SECTION_4_PARAGRAPH_5")}</Paragraph>
					</RecommendedResourcesPostItem>
					<RecommendedResourcesPostItem lg={4} utility={{ sm: { border: { left: { width: 0 } } } }}>
						<Block>
							<Image src={t("SECTION_5_IMAGE")} />
						</Block>
						<Paragraph sx={{ fontSize: "xl", fontWeight: 500 }}>{t("SECTION_5_TITLE")}</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }} spacing={{ mt: 2 }}>
							{t("SECTION_5_PARAGRAPH_1")}
						</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }}>{t("SECTION_5_PARAGRAPH_2")}</Paragraph>
						<Paragraph sx={{ fontColor: "black--60" }}>{t("SECTION_5_PARAGRAPH_3")}</Paragraph>
					</RecommendedResourcesPostItem>
				</RecommendedResourcesPost>
			</LandingSimpleSection>
			<ContactForm />
		</SiteLayout>
	);
};

Contact.getInitialProps = propsResolver({
	countries,
});

export default pipe(Contact, withErrorHandling, WithServices);
